/* eslint-disable array-bracket-newline, react-hooks/exhaustive-deps, indent, no-undef, no-unused-vars, react/jsx-key */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer } from 'react-virtualized';
import { FixedSizeList } from 'react-window';
import {
  useTable,
  useSortBy,
  useBlockLayout,
  useResizeColumns,
  useRowState,
} from 'react-table';
import Loader from '../../components/common/Loader';
import ColumnsList from './ColumnsList';
import Image from '../../atoms/Image';
import Text from '../../atoms/Text';
import shevron_down from '../../assets/images/shevron_down.svg';
import arrow_right from '../../assets/images/arrow_right.svg';
import shevron_up from '../../assets/images/shevron_up.svg';
import download from '../../assets/images/download.svg';
import pencil from '../../assets/images/pencil.svg';
import useDownloadCSVData from './useDownloadCSVData';
import useUpscale from './useUpscale';

import 'react-virtualized/styles.css';
import './styles.scss';

const scrollbarWidth = 4;

interface IDesktopTable {
  hiddenColumnsKey: string;
  dataKey: string;
  title: string;
  tableColumnsWidth: { [key: string]: number };
  tableWidth: number;
  columns: any[];
  defaultSortedColumn?: { id: string; desc: boolean };
  data: any[];
  csvData?: any[];
  loading?: boolean;
  placeholderText?: string;
  hiddenColumns?: string[];
  hoveredKey?: string;
  getClassNameForRow?(row: any): string;
  renderCell(cell: any, index: number): React.ReactNode;
  renderTableControls?(): JSX.Element;
  onRowClick?(row: any): void;
}

const DesktopTable = ({
  hiddenColumnsKey,
  hiddenColumns = [],
  dataKey,
  title,
  tableColumnsWidth,
  tableWidth,
  columns,
  defaultSortedColumn,
  data,
  csvData,
  loading,
  hoveredKey,
  getClassNameForRow,
  renderCell,
  renderTableControls,
  onRowClick,
  placeholderText,
}: IDesktopTable) => {
  const tableHiddenLC = localStorage.getItem(hiddenColumnsKey) || 'null';
  const reducer = (newState: any, action: any) => {
    switch (action.type) {
      case 'fitMaxWidth':
        return {
          ...newState,
          columnResizing: {
            columnWidths: {
              ...newState.columnResizing.columnWidths,
              ...action.payload,
            },
          },
        };
      default:
        return newState;
    }
  };

  const tableRef = useRef<HTMLDivElement>(null);
  const contentsRef = useRef<HTMLDivElement>(null);

  const [isHeaderEditable, setHeaderEditable] = useState(false);

  const sortees = useMemo(
    () => (defaultSortedColumn ? [defaultSortedColumn] : []),
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      disableSortRemove: true,
      initialState: {
        // @ts-ignore
        sortBy: sortees,
        hiddenColumns: JSON.parse(tableHiddenLC) || hiddenColumns,
      },
      stateReducer: reducer,
    },
    useSortBy,
    useBlockLayout,
    useResizeColumns,
    useRowState
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    totalColumnsWidth,
    allColumns,
    visibleColumns,
    dispatch,
    state,
  } = tableInstance;

  const { upscale, handleUpscale } = useUpscale(
    totalColumnsWidth,
    headerGroups,
    dispatch,
    tableWidth,
    tableRef,
    dataKey
  );
  const { handleDownload } = useDownloadCSVData(title, csvData || []);

  useEffect(() => {
    const tableWidthsLC = localStorage.getItem(dataKey);
    if (!tableWidthsLC) {
      handleUpscale();
    }
  }, []);

  // save to localStorage if resizing ended
  useEffect(() => {
    if ((state as any).columnResizing.isResizingColumn === null) {
      let updated = tableColumnsWidth;

      Object.keys((state as any).columnResizing.columnWidths).forEach(
        (item) => {
          updated = {
            ...updated,
            [item]: (state as any).columnResizing.columnWidths[item],
          };
        }
      );

      localStorage.setItem(dataKey, JSON.stringify(updated));
    }
  }, [(state as any).columnResizing]);

  const renderRow = React.useCallback(
    ({ index, style }) => {
      const row: any = rows[index];
      prepareRow(row);
      return (
        <div
          {...row.getRowProps()}
          style={{
            ...style,
            display: 'flex',
            ...(onRowClick ? { cursor: 'pointer' } : {}),
          }}
          className={`tr ${
            getClassNameForRow ? getClassNameForRow(row.original) : ''
          }`}
          {...(hoveredKey
            ? {
                'data-hovered': row.original[hoveredKey],
              }
            : {})}
          onClick={() => onRowClick && onRowClick(row.original)}
        >
          {row.cells.map((cell: any, cellIndex: any) => (
            <div
              className='td'
              key={index + cellIndex}
              {...cell.getCellProps()}
            >
              {renderCell(cell, index + 1)}
            </div>
          ))}
        </div>
      );
    },
    [prepareRow, rows, renderCell]
  );

  const onBind = (contents: HTMLDivElement | null, signal: AbortSignal) => {
    contents?.addEventListener(
      'mouseover',
      (e: any) => {
        if (e.target) {
          const closestRow = e.target.closest('.tr') as HTMLDivElement;
          if (closestRow) {
            const wallet = closestRow.dataset.hovered;
            const data = contents.dataset;
            if (data.lockedWallet !== wallet) {
              data.lockedWallet = wallet;
              const hoveredElements = contents.querySelectorAll('.hovered');
              const elementsToHover = contents.querySelectorAll(
                `[data-hovered='${wallet}']`
              );
              hoveredElements.forEach((element) => {
                element.classList.remove('hovered');
              });
              elementsToHover.forEach((element) => {
                element.classList.add('hovered');
              });
            }
          }
        }
      },
      { signal }
    );

    contents?.addEventListener(
      'mouseleave',
      () => {
        contents.dataset.lockedWallet = '';
        const elements = contents.querySelectorAll('.hovered');
        elements.forEach((element) => {
          element.classList.remove('hovered');
        });
      },
      { signal }
    );
  };

  /**
   * Handle hover logic
   */
  useEffect(() => {
    const controller = new AbortController();
    if (contentsRef.current && hoveredKey) {
      const { signal } = controller;
      window.requestIdleCallback(() => {
        onBind(contentsRef.current, signal);
      });
    }
    return () => controller.abort();
  }, [contentsRef, onBind, hoveredKey]);

  const dataCheck = !!(!loading && data.length);

  return (
    <div className='table_desktop' ref={tableRef}>
      {isHeaderEditable && (
        <ColumnsList
          onHide={() => setHeaderEditable(false)}
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          hiddenColumnsKey={hiddenColumnsKey}
        />
      )}
      <div className='table_controls'>
        {dataCheck && (
          <Image
            className='btn'
            size='s'
            src={pencil}
            onClick={() => setHeaderEditable(!isHeaderEditable)}
            data-tip='Edit columns'
          />
        )}
        {csvData && dataCheck && (
          <Image
            className='btn'
            size='s'
            src={download}
            onClick={handleDownload}
            data-tip='Download'
          />
        )}
        {upscale && dataCheck && (
          <Image
            className='btn'
            size='s'
            src={arrow_right}
            onClick={handleUpscale}
            data-tip='Fit table to max width'
          />
        )}
        {renderTableControls && renderTableControls()}
      </div>
      {loading ? (
        <div className='tableCell centrify'>
          <Loader />
        </div>
      ) : !data.length ? (
        <div className='tableCell centrify'>
          {placeholderText ?? 'No data found'}
        </div>
      ) : (
        <div className='table' {...getTableProps()}>
          <div className='thead'>
            {headerGroups.map((headerGroup) => (
              <div className='tr thr' {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <div
                    className='th'
                    key={JSON.stringify(column)}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <Text
                      size={12}
                      className={column.attachRight ? 'attach_right' : ''}
                    >
                      {column.render('Header')}
                    </Text>
                    {!(column as any).disableSortBy && (
                      <span>
                        <div className='img'>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Image src={shevron_down} size='s' />
                            ) : (
                              <Image src={shevron_up} size='s' />
                            )
                          ) : null}
                        </div>
                      </span>
                    )}
                    <div
                      {...column.getResizerProps()}
                      onClick={(e) => e.stopPropagation()}
                      className='resizer'
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div
            className='tbody'
            {...getTableBodyProps()}
            ref={contentsRef}
            {...(hoveredKey
              ? {
                  'data-locked-hovered': '',
                }
              : {})}
          >
            <AutoSizer>
              {({ height }) => (
                <FixedSizeList
                  height={height}
                  itemCount={rows.length}
                  itemSize={28}
                  width={totalColumnsWidth + scrollbarWidth}
                  className='table_insights'
                >
                  {renderRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DesktopTable);
